import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import type { ApiImage } from '../lib/media';
import { Card, type CardTone } from './Card';
import { Meta } from './Meta';
import { ReadMore } from './ReadMore';
import { StatusBadge, type StatusTone } from './StatusBadge';
import { Thumb, type ThumbKind } from './Thumb';
import styles from './ui.module.css';

/**
 * Строка списка имущества: предмет, питомец, способность.
 *
 * Одна на три домена. До неё карточка была переписана трижды почти дословно
 * (MyInventory, MyPets, MyAbilities), и копии успели разъехаться: у предмета
 * и питомца картинка рисовалась голым <img>, у способности — компонентом
 * Photo с другим радиусом и просмотром на весь экран; поиск у склада стоял
 * под пустым состоянием, у питомцев — над ним.
 *
 * Домена компонент не знает: сюда приходят уже готовые значок, подпись и
 * кнопки. Что во что превращается, решает propertyKit.
 */

interface PropertyCardProps {
  image: ApiImage | null;
  /** Чья это вещь: кадр без фотографии носит её значок. */
  kind?: ThumbKind;
  /**
   * Что листать свайпом при открытии на весь экран. Не передали — листается
   * одно это фото. Кнопка в кнопке тут не возникает: PropertyCard стоит на
   * Card, а это div, не CardLink.
   */
  gallery?: (ApiImage | null | undefined)[];
  title: string;
  /**
   * Знак состояния. Необязателен: в чужом досье всё видимое уже прошло
   * проверку, и подписывать каждую строку словом «в карточке» незачем.
   * Без него карточка стоит нейтральным контуром — состояния у неё нет.
   */
  badge?: string;
  tone?: StatusTone;
  /** Мелкие факты одной строкой: вид, номер версии, метки, срок. */
  meta?: ReactNode[];
  description: string | null;
  /** Что означает состояние и что с этим делать. */
  hint?: string | null;
  /**
   * Своё у сущности ВНУТРИ шапки, под строкой фактов: метки предмета. Полосой
   * во всю ширину карточки они уезжали под описание и читались как отдельный
   * раздел, хотя это свойство самой вещи.
   */
  headExtra?: ReactNode;
  /** Своё у сущности ПОД описанием: ссылка на источник способности. */
  extra?: ReactNode;
  /**
   * Кнопок может не быть вовсе: стартовая способность — это девятый пункт
   * анкеты, строки в базе за ней нет, и делать с ней здесь нечего.
   */
  actions?: ReactNode;
}

/**
 * Тон карточки по состоянию вещи. Экспортируется, потому что копию этой
 * карты уже завели у себя другие экраны, а разъехаться ей нельзя.
 */
export const CARD_TONE: Record<StatusTone, CardTone> = {
  published: 'published',
  pending: 'pending',
  rejected: 'rejected',
  archived: 'archived',
  // Предупреждение контур не красит: это состояние настройки, а не вещи.
  warning: 'default',
};

const HINT_TONE: Partial<Record<StatusTone, string>> = {
  pending: styles.propHintPending,
  rejected: styles.propHintRejected,
};

export function PropertyCard({
  image,
  kind,
  gallery,
  title,
  badge,
  tone,
  meta,
  description,
  hint = null,
  headExtra,
  extra,
  actions,
}: PropertyCardProps) {
  const hasMeta = meta !== undefined && meta.some((row) => row !== null && row !== undefined);

  return (
    <Card tone={tone === undefined ? 'default' : CARD_TONE[tone]} padded={false}>
      <div className={styles.propCard}>
        <div className={styles.propHead}>
          {/* Кадр стоит ВСЕГДА, и у вещи без фото в нём заглушка. Условная
            миниатюра давала две разные карточки на один вид: с кадром и без,
            и в одном списке они не выстраивались в колонку. */}
          <Thumb image={image} kind={kind} size="card" alt={title} gallery={gallery ?? [image]} />
          <div className={styles.propHeadText}>
            <div className={styles.propTitleRow}>
              <span className={styles.propTitle}>{title}</span>
              {badge === undefined || tone === undefined ? null : (
                <StatusBadge tone={tone}>{badge}</StatusBadge>
              )}
            </div>
            {hasMeta ? (
              <div className={styles.propMeta}>
                <Meta>{meta}</Meta>
              </div>
            ) : null}
            {headExtra === undefined ? null : (
              <div className={styles.propHeadExtra}>{headExtra}</div>
            )}
          </div>
        </div>

        {description !== null && description !== '' ? (
          // Свёрнуто до трёх строк: описание предмета пишут абзацем, и в списке
          // из десяти вещей развёрнутые описания превращают его в полотно.
          <div className={styles.propText}>
            <ReadMore>{description}</ReadMore>
          </div>
        ) : null}

        {extra !== undefined ? <div className={styles.propExtra}>{extra}</div> : null}

        {hint === null ? null : (
          <div className={clsx(styles.propHint, tone !== undefined && HINT_TONE[tone])}>{hint}</div>
        )}

        {actions === undefined ? null : <div className={styles.propActions}>{actions}</div>}
      </div>
    </Card>
  );
}
